/**
 * IndexedDB draft service.
 * Saves and restores form drafts keyed by maintenance UID.
 * Note: IndexedDB is subject to browser storage eviction — see architecture docs.
 */

const DB_NAME = 'pwa-maintenance-drafts'
const DB_VERSION = 1
const STORE_NAME = 'drafts'

function openDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION)
        request.onupgradeneeded = (e) => {
            const db = e.target.result
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, {keyPath: 'uid'})
            }
        }
        request.onsuccess = (e) => resolve(e.target.result)
        request.onerror = (e) => reject(e.target.error)
    })
}

/**
 * Save a form draft for a given maintenance UID.
 * @param {string} uid - Maintenance UID
 * @param {object} data - Full form state to persist
 */
export async function saveDraft(uid, data) {
    const db = await openDB()
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        const store = tx.objectStore(STORE_NAME)
        store.put({uid, data, savedAt: new Date().toISOString()})
        tx.oncomplete = () => resolve()
        tx.onerror = (e) => reject(e.target.error)
    })
}

/**
 * Load a saved draft for a given maintenance UID.
 * @param {string} uid - Maintenance UID
 * @returns {object|null} Saved form state, or null if no draft exists
 */
export async function loadDraft(uid) {
    const db = await openDB()
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly')
        const store = tx.objectStore(STORE_NAME)
        const request = store.get(uid)
        request.onsuccess = (e) => resolve(e.target.result ?? null)
        request.onerror = (e) => reject(e.target.error)
    })
}

/**
 * Delete a draft once the form has been submitted.
 * @param {string} uid - Maintenance UID
 */
export async function deleteDraft(uid) {
    const db = await openDB()
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        const store = tx.objectStore(STORE_NAME)
        store.delete(uid)
        tx.oncomplete = () => resolve()
        tx.onerror = (e) => reject(e.target.error)
    })
}
